// ===========================================================================
// Module H — Member Outreach Campaign Engine · Service layer
//
// Hoy lee outreach.json de los dumps diarios de DataDashCareManager.
// Los intentos previos de contacto y el canal preferido NO existen en el dump:
// se derivan DETERMINÍSTICAMENTE desde cipId para que la UI sea estable.
// SIMULADO hasta que el backend exponga el schema outreach
// (TAB_CAMPAIGN_TARGETS / TAB_OUTREACH_LOG).
// ===========================================================================

/**
 * Canal preferido derivado de la contactabilidad real del dump
 * (teléfono > email), con desempate determinístico por cipId.
 */
function derivePreferredChannel(p) {
  if (p.hasPhone && p.hasEmail) return Number(p.cipId) % 2 === 0 ? "Call" : "SMS";
  if (p.hasPhone) return "Call";
  if (p.hasEmail) return "Email";
  return null;
}

/** Intentos previos de outreach — SIMULADO (cipId % 3). */
function derivePriorAttempts(cipId) {
  return Number(cipId) % 3;
}

/** Prioridad de la cola: tiempo sin visita + severidad clínica. */
function derivePriorityScore(p) {
  const timeframeWeight =
    { "12+ Months": 4, "9 Months": 3, "6 Months": 2, "3 Months": 1, "<3 Months": 0 }[
      p.timeSinceVisit
    ] ?? 0;
  const statusWeight =
    { "End of Life": 3, Complex: 3, "At Risk": 2, Chronic: 2, Acute: 1, Well: 0 }[
      p.healthStatus
    ] ?? 0;
  const contactable = p.hasPhone || p.hasEmail ? 1 : 0;
  return timeframeWeight * 3 + statusWeight + contactable;
}

/**
 * Obtiene la cola de outreach: miembros sin visita reciente, con
 * contactabilidad, estado de salud y campos de campaña derivados.
 *
 * FUTURO ENDPOINT BACKEND: GET /api/Outreach/targets?locationKey={key}
 * (respaldado por outreach.TAB_CAMPAIGN_TARGETS + TAB_OUTREACH_LOG).
 * Hoy: dump JSON local + derivación determinística simulada.
 *
 * @param {string} locationKey - Key de la location (e.g. "fort_myers")
 *
 * Ejemplo de respuesta:
 * {
 *   "stats": { "patientsToContact": 796, "withPhone": 610, "withEmail": 486, "contactable": 632, ... },
 *   "patients": [
 *     {
 *       "cipId": 93750482868, "name": "JEROME, WISLAND",
 *       "timeSinceVisit": "12+ Months", "phone": "(239) 745-2531",
 *       "email": "JESSUPER45@GMAIL.COM", "healthStatus": "End of Life",
 *       "conditions": "Female / Male Breast Cancer, Fibromyalgia +1",
 *       // —— derivados (simulados) ——
 *       "preferredChannel": "Call", "priorAttempts": 1, "priorityScore": 16
 *     }
 *   ]
 * }
 *
 * @returns {Promise<{stats: Object, patients: Object[]}>}
 */
export async function getOutreachQueue(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashCareManager/${locationKey}/outreach.json`
  );
  const data = module.default || module;

  const patients = (data.patients || []).map((p) => ({
    ...p,
    preferredChannel: derivePreferredChannel(p),
    priorAttempts: derivePriorAttempts(p.cipId),
    priorityScore: derivePriorityScore(p),
  }));

  // Cola priorizada: mayor tiempo sin visita y mayor severidad primero
  patients.sort((a, b) => b.priorityScore - a.priorityScore);

  return { stats: data.stats || {}, patients };
}
